import { useEffect, useRef, useState } from 'react';
import { ANALYTICS, ENABLED_MODULES, ML, TRACEABILITY } from '@/constants/modules';
import { AuthContext } from './AuthContext';
import { ApiClient } from '@/lib/http/ApiClient';
import { AuthApiClient } from '@/features/auth/api/AuthApiClient';
import { StorageService } from '@/lib/StorageService';
import { setSentryUser, clearSentryUser } from '@/lib/logging/sentry';
import { AuthError, ErrorCategory } from '@/lib/errors/AppError';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Map a failed login Result to the right i18n key instead of collapsing
 * every failure into "invalid credentials" (FE-3):
 * - 429  → rate-limit message (the orchestrator throttles login to 10/min;
 *          telling the user to retry immediately defeats the throttle)
 * - network/timeout (frontend-detected, name = ErrorCategory) → network message
 * - 5xx  → generic server-error message
 * - anything else (real 401 etc.) → invalid credentials, as before
 */
function loginErrorKey(code: number, name: string): string {
  if (code === 429) return 'errors.api.rateLimited';
  if (name === ErrorCategory.NETWORK_ERROR) return 'errors.network.generic';
  if (name === ErrorCategory.TIMEOUT_ERROR) return 'errors.network.timeout';
  if (code >= 500) return 'errors.api.serverError';
  return 'errors.auth.invalidCredentials';
}

/** How often coming back to the tab re-reads the profile, at most. */
const PROFILE_SYNC_MIN_INTERVAL_MS = 30_000;

function sameModules(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');
}

/**
 * Login stores role, modules and company once; the server keeps the real values.
 * Reads them from the profile and stores them. Returns true when something changed.
 */
async function storeProfile(service: StorageService, client: AuthApiClient): Promise<boolean> {
  const result = await client.getProfile();
  if (!result.ok) return false;

  const { role, modules, companyName } = result.data.profile;
  const allowed = (modules ?? []).filter((m) => ENABLED_MODULES.includes(m));
  const company = companyName || null;
  if (
    service.getRole() === role &&
    service.getCompany() === company &&
    sameModules(service.getModules(), allowed)
  ) {
    return false;
  }

  service.setRole(role);
  if (company) {
    service.setCompany(company);
  } else {
    service.removeCompany();
  }
  service.setModules(allowed);
  return true;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [tokenService, setTokenService] = useState<StorageService | null>(null);
  const [authClient, setAuthClient] = useState<AuthApiClient | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false); // ✅
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const lastProfileSyncRef = useRef(0);

  useEffect(() => {
    const service = new StorageService();

    // Callback to notify when refresh state changes
    const handleRefreshStateChange = (isRefreshing: boolean) => {
      setIsRefreshingToken(isRefreshing);
    };

    // Define refresh function that will be used by ApiClient
    const handleTokenRefresh = async () => {
      if (import.meta.env.DEV) {
        console.log('[AuthProvider] Token refresh triggered by ApiClient');
      }

      // Clear old token
      service.removeAccessToken();

      // Create temporary client without refresh to avoid infinite loop
      const tempApiClient = new ApiClient(
        import.meta.env.VITE_PROD_BACK_END,
        () => service.getAccessToken(),
        null,
        null
      );
      const tempAuthClient = new AuthApiClient(tempApiClient);

      // Call refresh endpoint
      const result = await tempAuthClient.refresh();

      if (result.ok) {
        service.setAccessToken(result.data.accessToken);
        setIsAuthenticated(true);
      } else {
        // Refresh failed - logout user
        service.clear();
        setIsAuthenticated(false);
        throw new Error('Token refresh failed');
      }
    };

    const apiClient = new ApiClient(
      import.meta.env.VITE_PROD_BACK_END,
      () => service.getAccessToken(),
      handleTokenRefresh,
      handleRefreshStateChange
    );
    const client = new AuthApiClient(apiClient);
    setAuthClient(client);
    setTokenService(service);

    checkAuth(service, client);
    setIsLoading(false);
    setIsAuthInitialized(true);
  }, []);

  const login = async (email: string, password: string, remember: boolean) => {
    if (!authClient) {
      throw new Error('Auth client not initialized. Please try again.');
    }
    const service = new StorageService();
    service.setRememberMe(remember);
    const result = await authClient.login({ email, password });

    if (!result.ok) throw new AuthError(t(loginErrorKey(result.code, result.name)));

    const allowed = result.data.modules
      ? result.data.modules.filter((m) => ENABLED_MODULES.includes(m))
      : [TRACEABILITY, ML, ANALYTICS].filter((m) => ENABLED_MODULES.includes(m));
    service.setModules(allowed);

    service.setAccessToken(result.data.accessToken);
    if (result.data.role) {
      service.setRole(result.data.role);
    }
    if (result.data.companyName) {
      service.setCompany(result.data.companyName);
    }
    service.setUserMail(email);

    setTokenService(service);
    setIsAuthenticated(true);

    // Set Sentry user context for error tracking
    setSentryUser({
      id: email, // Using email as ID since we don't have user ID in response
      email: email,
      username: email.split('@')[0], // Use email username part
    });
  };

  const refresh = async () => {
    if (!tokenService || !authClient) {
      return;
    }

    tokenService.removeAccessToken();
    const result = await authClient.refresh();
    if (result.ok) {
      tokenService.setAccessToken(result.data.accessToken);
      setIsAuthenticated(true);
    } else {
      logout();
    }
  };

  const checkAuth = async (service: StorageService, client: AuthApiClient) => {
    if (!service || !client || !service.getAccessToken()) {
      setIsAuthenticated(false);
      setIsAuthChecked(true);
      return;
    }

    const result = await client.verifyToken();

    if (result.ok) {
      // Before routing: ModuleRoute must see the modules the user has now.
      lastProfileSyncRef.current = Date.now();
      if (await storeProfile(service, client)) setTokenService(new StorageService());
      setIsAuthenticated(true);
    } else {
      // If verification failed (including after automatic refresh attempt by ApiClient),
      // clear storage and logout
      service.clear();
      setIsAuthenticated(false);
    }
    setIsAuthChecked(true);
  };

  /**
   * Keeps role, modules and company in step with the server (on load, here, and when
   * the user comes back to the tab), so an admin's change to this user shows without
   * a new login. The storage identity changes only when something changed: that
   * re-renders every reader and recreates the API clients, so data scoped by company
   * is fetched again.
   */
  const reloadProfile = async () => {
    if (!tokenService?.getAccessToken() || !authClient) return;
    lastProfileSyncRef.current = Date.now();
    if (await storeProfile(tokenService, authClient)) setTokenService(new StorageService());
  };

  // Changes made by another admin while this tab was open: re-read when the user comes back.
  useEffect(() => {
    if (!isAuthenticated || !tokenService || !authClient) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !tokenService.getAccessToken()) return;
      if (Date.now() - lastProfileSyncRef.current < PROFILE_SYNC_MIN_INTERVAL_MS) return;
      lastProfileSyncRef.current = Date.now();
      void storeProfile(tokenService, authClient).then((changed) => {
        if (changed) setTokenService(new StorageService());
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, tokenService, authClient]);

  /** Asked to the server every time (see `AdminRoute`): nothing about it is kept in the browser. */
  const isAdmin = async (): Promise<boolean> => {
    if (!authClient) return false;

    // If the check fails, ApiClient will already have attempted a token refresh
    const result = await authClient.isAdmin();
    return result.ok && result.data.isAdmin;
  };

  const logout = async () => {
    try {
      await authClient?.logout();
    } catch {
      // Best-effort: network failure must not block local session clear
    }
    tokenService?.clear();
    setIsAuthenticated(false);

    // Clear Sentry user context on logout
    clearSentryUser();
  };

  return (
    <AuthContext.Provider
      value={{
        tokenService,
        login,
        refresh,
        logout,
        isAuthenticated,
        isLoading,
        isAuthInitialized,
        isAuthChecked,
        isAdmin,
        isRefreshingToken,
        reloadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
